import React, { useMemo } from 'react';
import { gql } from 'urql';
import { useAuthenticatedQuery } from 'client/hooks';
import {
    FollowerCollegesForPlayersQuery,
    FollowerCollegesForPlayersQueryVariables,
    FollowerCollegesForPlayersDocument,
} from '../../../generated/spin-graphql';
import { useAppState } from 'client/providers/AppProvider';
import { PlayerFollowers } from './PlayerFollowers';
import { CONVERT_TO_MILES } from 'shared/shared.constants';

gql`
    query FollowerCollegesForPlayers(
        $closeSearch: espin_college_bool_exp!
        $farSearch: espin_college_bool_exp!
        $playerUserId: String!
        $date: timestamptz!
    ) {
        following: espin_follow(
            where: { player_user_id: { _eq: $playerUserId }, follow_type: { _eq: player_follows_college } }
        ) {
            college_id
        }
        close_follow: espin_follow(
            where: {
                college: $closeSearch
                player_user_id: { _eq: $playerUserId }
                follow_type: { _eq: college_follows_player }
            }
        ) {
            ...PlayerFollowers
        }
        close_aggregate: espin_follow_aggregate(
            where: {
                college: $closeSearch
                player_user_id: { _eq: $playerUserId }
                follow_type: { _eq: college_follows_player }
                created_at: { _gt: $date }
            }
        ) {
            aggregate {
                count
            }
        }
        far_follow: espin_follow(
            where: {
                college: $farSearch
                player_user_id: { _eq: $playerUserId }
                follow_type: { _eq: college_follows_player }
            }
        ) {
            ...PlayerFollowers
        }
        far_aggregate: espin_follow_aggregate(
            where: {
                college: $farSearch
                player_user_id: { _eq: $playerUserId }
                follow_type: { _eq: college_follows_player }
                created_at: { _gt: $date }
            }
        ) {
            aggregate {
                count
            }
        }
    }
`;

type Props = {};
const backUpDate = new Date().toISOString();
export const PlayerFollowersContainer: React.FC<Props> = ({}) => {
    const { user } = useAppState();

    const { isReady, data } = useAuthenticatedQuery<
        FollowerCollegesForPlayersQuery,
        FollowerCollegesForPlayersQueryVariables
    >(
        {
            query: FollowerCollegesForPlayersDocument,
            variables: {
                closeSearch: {
                    address: {
                        location: {
                            _st_d_within: {
                                distance: 100 * CONVERT_TO_MILES,
                                from: {
                                    type: 'Point',
                                    coordinates: user?.player_profile?.address?.location?.['coordinates'] ?? [0, 0],
                                },
                            },
                        },
                    },
                },
                farSearch: {
                    _not: {
                        address: {
                            location: {
                                _st_d_within: {
                                    distance: 100 * CONVERT_TO_MILES,
                                    from: {
                                        type: 'Point',
                                        coordinates: user?.player_profile?.address?.location?.['coordinates'] ?? [0, 0],
                                    },
                                },
                            },
                        },
                    },
                },
                playerUserId: user?.id,
                date: user.last_login ?? backUpDate,
            },
            pause: !user,
        },
        ['espin_follow'],
    );

    const { close_aggregate, close_follow, far_aggregate, far_follow } = data ?? {};
    const listOfFollows = useMemo(() => data?.following.map((f) => f.college_id), [data?.following]);
    return (
        <>
            {isReady && (
                <PlayerFollowers
                    listOfFollows={listOfFollows}
                    closeColleges={close_follow}
                    farColleges={far_follow}
                    newFollowers={close_aggregate?.aggregate.count + far_aggregate?.aggregate.count}
                    totalFollowers={close_follow.length + far_follow.length}
                />
            )}
        </>
    );
};
